import * as Sentry from "@sentry/react";
import { Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { isCustomerUser } from '@/integrations/supabase/session';
import { useTenant } from '@/hooks/useTenant';
import { useVisibility } from '@/hooks/useVisibility';
import { Layout } from '@/components/Layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useEffect } from 'react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const { company, isLoading: isLoadingTenant, isTechOnly } = useTenant();
  const { canManageSettings } = useVisibility();
  const location = useLocation();

  const companyId = company?.id;
  const companyName = company?.name;

  useEffect(() => {
    if (companyId) {
      Sentry.setTag("company_id", companyId);
      Sentry.setTag("company_name", companyName);
    }
  }, [companyId, companyName]);

  if (isLoading || isLoadingTenant) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Customer-portal logins have no profile or tenant; the CRM is not for them.
  if (isCustomerUser(user)) {
    return <Navigate to="/portal" replace />;
  }

  // Technician-only logins belong on the Steddy app: the CRM never renders for them.
  if (isTechOnly && location.pathname !== '/download-app') {
    return <Navigate to="/download-app" replace />;
  }

  // Send admins of a company that has not finished the setup wizard to it.
  // (setup_completed_at is NULL for new tenants; "My Company" is the legacy signal.)
  const setupPending = !!company && (company.setup_completed_at == null || companyName === 'My Company');
  if (setupPending && canManageSettings && location.pathname !== '/company-setup') {
    return <Navigate to="/company-setup" replace />;
  }

  if (requireAdmin && !canManageSettings) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              Only administrators can access this page.
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return <>{children}</>;
}
